import React from "react";
import { injectIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import { Line } from "react-chartjs-2";
import "chartjs-adapter-moment";

import moment from "moment";

import useIsAr from "../../Hooks/useIsAr";
import { getKPITypeStatusColor } from "../../helpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const types = {
  annual: {
    startOf: "year",
    timeScale: "year",
    stepSize: 1,
  },
  monthly: {
    startOf: "month",
    timeScale: "month",
    stepSize: 1,
  },
  "semi-annual": {
    startOf: "quarter",
    timeScale: "quarter",
    stepSize: 2,
  },
  quarterly: {
    startOf: "quarter",
    timeScale: "quarter",
    stepSize: 1,
  },
  custom: {
    startOf: "day",
    timeScale: "day",
    stepSize: 1,
  },
};

function getHalfLabel(val, isAr) {
  const date = moment(val, "Q - YYYY");
  const month = date.month() + 1;
  return month <= 6
    ? isAr
      ? `النصف ١ - ${date.format("YYYY")}`
      : `H1 - ${date.format("YYYY")}`
    : isAr
    ? `النصف ٢ - ${date.format("YYYY")}`
    : `H2 - ${date.format("YYYY")}`;
}

function getQuarterLabel(val, isAr) {
  const date = moment(val, "Q - YYYY");
  const quarter = date.quarter();
  const arabicNumbers = ["", "١", "٢", "٣", "٤"];
  return isAr
    ? `الربع ${arabicNumbers[quarter]} - ${date.format("YYYY")}`
    : `Q${quarter} - ${date.format("YYYY")}`;
}

function MeasuresChart({
  intl,
  data,
  statuses,
  kpiFrequency,
  compact,
  isAccumulative = false,
  HasNoTargetBaseline,
}) {
  const frequency = kpiFrequency === "custom" ? kpiFrequency : "monthly";
  const [isAr, locale] = useIsAr();
  const themeColor = useSelector((store) => store.theme.primary);

  moment.locale(locale);
  const fontSize = compact ? 10 : 13;
  const valuelabel = intl.formatMessage({
    id: "chart-q.actual-value",
    defaultMessage: "chart-q.actual-value",
  });
  const targetlabel = intl.formatMessage({
    id: "chart-q.target",
    defaultMessage: "chart-q.target",
  });
  const maxTargetKey = isAccumulative ? "AccumulativeTarget" : "Target";
  const maxValueKey = isAccumulative ? "AccumulativeValue" : "Value";
  const minValue = Math.min(
    ...data?.map((e) => {
      if (e[maxTargetKey] !== null && e[maxValueKey] !== null) {
        return e[maxTargetKey] < e[maxValueKey]
          ? e[maxTargetKey]
          : e[maxValueKey];
      } else if (e[maxTargetKey] === null && e[maxValueKey] !== null) {
        return e[maxValueKey];
      } else if (e[maxTargetKey] !== null && e[maxValueKey] === null) {
        return e[maxTargetKey];
      }
      return 0;
    })
  );
  const maxValue = Math.max(
    ...data?.map((e) => {
      if (e[maxTargetKey] !== null && e[maxValueKey] !== null) {
        return e[maxTargetKey] > e[maxValueKey]
          ? e[maxTargetKey]
          : e[maxValueKey];
      } else if (e[maxTargetKey] === null && e[maxValueKey] !== null) {
        return e[maxValueKey];
      } else if (e[maxTargetKey] !== null && e[maxValueKey] === null) {
        return e[maxTargetKey];
      }
      return 0;
    })
  );
  const options = {
    plugins: {
      locale,
      legend: false,
      filler: {
        propagate: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        rtl: isAr,
        // position: "nearest",
        // intersect: true,
        usePointStyle: true,
        callbacks: {
          title: () => {},
        },
        bodySpacing: 4,
        boxPadding: 4,
        borderWidth: 3,
        cornerRadius: 5,
        bodyFont: {
          family: "Cairo",
          size: 13,
        },
      },
    },
    elements: {
      point: {
        radius: 5,
        borderWidth: 3,
        hoverRadius: 7,
      },
    },
    scales: {
      y: {
        position: isAr ? "right" : "left",
        suggestedMin: isFinite(minValue) ? minValue - minValue / 2 : 0,
        // suggestedMax:
        //   maxValue > 0
        //     ? maxValue +
        //       (maxValue / data?.length > 20 ? maxValue / data?.length : 20)
        //     : maxValue,
        ticks: {
          // stepSize: 1,
          display: true,
          beginAtZero: true,
          font: {
            family: "Cairo",
            size: fontSize,
          },
        },
      },
      x: {
        type: "time",
        reverse: isAr,
        time: {
          unit: types[frequency]?.timeScale,
          stepSize: types[frequency]?.stepSize,
          displayFormats: {
            year: "YYYY",
            month: compact ? "M/YY" : "MMM-YYYY",
            day: "DD-MM-YYYY",
          },
        },
        ticks: {
          autoSkip: false,
          source: "data",
          beginAtZero: true,
          maxRotation: 45,
          minRotation: 45,
          callback:
            frequency === "semi-annual" || frequency === "quarterly"
              ? function (val) {
                  // Hide the label of every 2nd dataset
                  return frequency === "semi-annual"
                    ? getHalfLabel(val, isAr)
                    : getQuarterLabel(val, isAr);
                }
              : undefined,
          font: {
            family: "Cairo",
            size: fontSize,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
      caretSize: false,
    },
    tension: 0.2,
    responsive: true,
    maintainAspectRatio: false,
  };

  const generateData = () => {
    const values = [];
    const targets = [];

    data.forEach((item, index) => {
      const _date = moment(new Date(item.DueDate)).startOf(
        types[frequency]?.startOf
      );
      values.push({
        x: _date,
        y: isAccumulative ? item?.AccumulativeValue : item.Value,
      });
      targets.push({
        x: _date,
        y: isAccumulative ? item?.AccumulativeTarget : item.Target,
      });
    });

    return {
      datasets: [
        {
          id: "value",
          label: valuelabel,
          borderColor: "#cecece",
          pointBorderColor: function (context) {
            var index = context.dataIndex;
            return getKPITypeStatusColor(statuses, data[index]?.Status);
          },
          pointBackgroundColor: "#fff",
          pointBorderWidth: 3,
          pointHoverBorderWidth: 3,
          data: values,
        },
        {
          id: "target",
          label: targetlabel,
          borderColor: "black",
          pointBorderColor: "black",
          pointBackgroundColor: "#fff",
          pointBorderWidth: 3,
          pointHoverBorderWidth: 3,
          data: targets,
        },
      ],
    };
  };

  const plugins = [
    {
      id: "zoomCSS",
      beforeEvent(_, ctx) {
        const event = ctx.event;
        var zoom =
          window.getComputedStyle(document.querySelector("#root > .app"))
            .zoom || 1;
        if (zoom != 1) {
          event.x = event.x / zoom;
          event.y = event.y / zoom;
        }
      },
    },
  ];

  return <Line data={generateData()} options={options} plugins={plugins} />;
}

export default injectIntl(MeasuresChart);
